'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { ReportErrorButton } from '@/components/feedback/report-error-button';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { Alert01Icon } from '@/components/ui/icon/glyphs';
import { reportError, serializeForTelemetry, trackAction } from '@/lib/telemetry';
import { logger } from '@/lib/telemetry/logger';
interface ErrorBoundaryPageProps {
  /** The error caught by the Next.js error boundary. */
  error: Error & {
    digest?: string;
  };
  /** Reset function provided by Next.js to retry rendering. */
  reset: () => void;
  /**
   * Component/page name for telemetry context.
   * E.g. 'MarketsPage', 'OnboardingPage', 'PortfolioPage'.
   */
  component: string;
}
const IS_DEV: boolean = process.env.NODE_ENV !== 'production';

/**
 * Shared error boundary UI used by all route-level `error.tsx` files.
 *
 * Handles:
 * - Reporting the error to Better Stack via `reportError()` — splats every
 *   own-prop of the thrown value into `error_*` columns so structured
 *   diagnostics (e.g. SDK `PersistenceShapeViolationError` `.category` /
 *   `.path` / `.kind`) are searchable in production.
 * - Tracking the error boundary trigger via `trackAction()`.
 * - Rendering a consistent error UI with a retry button.
 * - In dev only, rendering the structured payload inline on the page so
 *   the diagnostic fields are visible without opening DevTools (Next.js'
 *   default overlay collapses object-shaped errors to "[object Object]").
 *
 * Usage in any error.tsx:
 * ```tsx
 * 'use client';
 * import { ErrorBoundaryPage } from '@/components/shared/error-boundary-page';
 * export default function MyPageError(props) { return <ErrorBoundaryPage {...props} component="MyPage" />; }
 * ```
 */
export function ErrorBoundaryPage(t0) {
  const $ = _c(34);
  const {
    error,
    reset,
    component
  } = t0;
  const t = useTranslations("errorBoundary");
  let t1;
  let t2;
  if ($[0] !== component || $[1] !== error) {
    t1 = () => {
      const url = typeof window !== "undefined" ? window.location.pathname : "unknown";
      logger.error(`error_boundary[${component}]`, {
        ...serializeForTelemetry(error),
        digest: error.digest ?? "none"
      });
      reportError(error, {
        component,
        digest: error.digest ?? "none"
      });
      trackAction("error_boundary_triggered", {
        route: url,
        component
      });
    };
    t2 = [error, component];
    $[0] = component;
    $[1] = error;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={Alert01Icon} className="size-10 text-feedback-error-content" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("title");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = <h2 className="text-heading-md">{t4}</h2>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t) {
    t6 = t("description");
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t6) {
    t7 = <p className="max-w-md text-body text-foreground-secondary">{t6}</p>;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t5 || $[14] !== t7) {
    t8 = <div className="flex flex-col gap-2">{t5}{t7}</div>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== component || $[17] !== error) {
    t9 = IS_DEV && <DevErrorPayload error={error} component={component} />;
    $[16] = component;
    $[17] = error;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t) {
    t10 = t("retry");
    $[19] = t;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== reset || $[22] !== t10) {
    t11 = <AnimatedButton onClick={reset} variant="secondary">{t10}</AnimatedButton>;
    $[21] = reset;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== component || $[25] !== error.digest) {
    t12 = <ReportErrorButton digest={error.digest} surface={component} />;
    $[24] = component;
    $[25] = error.digest;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11 || $[28] !== t12) {
    t13 = <div className="flex flex-wrap items-center justify-center gap-3">{t11}{t12}</div>;
    $[27] = t11;
    $[28] = t12;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13 || $[31] !== t8 || $[32] !== t9) {
    t14 = <div className="flex min-h-[40vh] flex-col items-center justify-center gap-5 p-8 text-center">{t3}{t8}{t9}{t13}</div>;
    $[30] = t13;
    $[31] = t8;
    $[32] = t9;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}

/**
 * Dev-only diagnostic block. Renders the structured serialization of the
 * caught error so the developer can see `category`, `path`, `kind`, and
 * every other own-prop the SDK puts on its error classes — directly on
 * the page, no DevTools required. Tree-shaken in production because
 * `IS_DEV` folds to `false` at the call site under `NODE_ENV=production`.
 */
function DevErrorPayload(t0) {
  const $ = _c(6);
  const {
    error,
    component
  } = t0;
  let t1;
  if ($[0] !== component || $[1] !== error) {
    const fields = serializeForTelemetry(error);
    const t2 = error.digest ?? "no-digest";
    let t3;
    if ($[3] !== component || $[4] !== t2) {
      t3 = <span className="text-foreground-tertiary">{component} • {t2}{"\n\n"}</span>;
      $[3] = component;
      $[4] = t2;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t1 = <pre data-testid="dev-error-payload" className="mt-2 max-w-2xl overflow-auto rounded-panel-inset border border-border-secondary bg-background-tertiary p-3 text-left text-caption text-foreground-secondary">{t3}{JSON.stringify(fields, null, 2)}</pre>;
    $[0] = component;
    $[1] = error;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}